import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Headphones, Loader2, Pause, Play, Sparkles } from "lucide-react";
import { S, SERIF, SANS, Eyebrow, Reveal, H2, Section, Grain } from "./slUi";
import { useSpeak, SpeakButton } from "./slSpeak";
import { useLayerMix } from "./useLayerMix";

const ROWS = [
  ["music", "Music", "The emotional environment"],
  ["soundscape", "Soundscape", "Water, air, the room around you"],
  ["spoken", "Spoken affirmation", "Clear, present words"],
  ["softer", "Softer affirmation", "Further away, more spacious"],
  ["whisper", "Whispered words", "Close, quiet, personal"],
  ["voice", "Your own voice", "Here, an example voice. In yours, your clone"],
];
// The automatic journey: layers ebb and flow, then space, then return.
const FLOW = [
  [0, "Music opens", { music: 1 }],
  [4, "The soundscape arrives", { soundscape: 0.9 }],
  [9, "Words begin", { spoken: 1 }],
  [17, "Softer words, further away", { softer: 1, spoken: 0.4 }],
  [24, "A whisper", { whisper: 1, spoken: 0 }],
  [30, "Your own voice", { voice: 1, softer: 0.3 }],
  [38, "Space", { voice: 0, whisper: 0, softer: 0, spoken: 0, music: 0.6 }],
  [45, "Return", { music: 1, voice: 0.9, whisper: 0.5 }],
];

const Row = ({ r, on, level, onTap, disabled }) => (
  <button type="button" onClick={onTap} disabled={disabled} data-testid={`sl-layer-${r[0]}`} aria-pressed={!!on}
    className="w-full flex items-center gap-4 rounded-xl px-4 py-3 text-left transition-[background-color,border-color] duration-500 disabled:opacity-60"
    style={{ background: on ? "rgba(224,169,122,0.12)" : "rgba(245,236,217,0.03)", border: `1px solid ${on ? S.gold : S.goldSoft}` }}>
    <span className="w-3 h-3 rounded-full shrink-0 transition-colors duration-500" style={{ background: on ? S.gold : "transparent", border: `1px solid ${S.gold}` }} />
    <span className="min-w-0 flex-1">
      <span className="block text-lg sm:text-xl" style={{ fontFamily: SERIF, color: S.cream }}>{r[1]}</span>
      <span className="block text-xs" style={{ fontFamily: SANS, color: S.dim }}>{r[2]}</span>
    </span>
    <span className="hidden sm:block w-32 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(245,236,217,0.08)" }}>
      <span className="block h-full rounded-full" style={{ width: `${Math.round((on ? level || 0 : 0) * 100)}%`, background: S.gold, transition: "width 120ms linear" }} />
    </span>
    <span className="text-[0.58rem] uppercase tracking-[0.22em] w-12 text-right" style={{ fontFamily: SANS, color: on ? S.gold : S.dim }}>{on ? "On" : "Add"}</span>
  </button>
);

export const Layers = () => {
  const m = useLayerMix();
  const g = useSpeak("layers");
  const [scene, setScene] = useState(null);
  const timers = useRef([]);
  const clear = () => { timers.current.forEach(clearTimeout); timers.current = []; };
  useEffect(() => clear, []);
  const tap = async (k) => {
    clear(); setScene(null);
    if (m.state !== "ready" && m.state !== "paused") { await m.start([k]); return; }
    if (m.state === "paused") m.resume();
    m.set(k, m.on[k] ? 0 : 1);
  };
  const flow = async () => {
    clear();
    if (m.state !== "ready") { if (m.state === "paused") m.resume(); else if (!(await m.start([]))) return; }
    ROWS.forEach(([k]) => m.set(k, 0, 0.6));
    FLOW.forEach(([at, label, mix]) => timers.current.push(setTimeout(() => { setScene(label); Object.entries(mix).forEach(([k, v]) => m.set(k, v, 2.2)); }, at * 1000 + 400)));
    timers.current.push(setTimeout(() => setScene("One experience, created around you."), 52000));
  };
  const busy = m.state === "loading";
  return (
    <Section tone="dark" id="sl-layers" testId="sl-layers" className="py-28 sm:py-36 overflow-hidden">
      <Grain />
      <div className="relative max-w-4xl mx-auto">
        <Reveal><Eyebrow light>Inside an installation</Eyebrow></Reveal>
        <Reveal delay={0.05}><H2 className="mt-4">Hear the <span className="" style={{ color: S.gold }}>layers</span> come together.</H2></Reveal>
        <Reveal delay={0.1}><p className="mt-5 max-w-2xl text-base sm:text-lg leading-relaxed" style={{ fontFamily: SANS, color: S.dim }}>
          Tap each layer to add it, or let the journey play. Nothing competes for your attention. Sometimes the words lead, sometimes the music leads, sometimes there is space.
        </p></Reveal>
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <button type="button" onClick={flow} disabled={busy} data-testid="sl-layers-flow" className="inline-flex items-center gap-3 rounded-full pl-2 pr-6 py-2 transition-transform duration-200 active:scale-95 disabled:opacity-60"
            style={{ background: S.gold, color: S.night, fontFamily: SANS, fontSize: "0.68rem", letterSpacing: "0.22em" }}>
            <span className="w-10 h-10 rounded-full inline-flex items-center justify-center" style={{ background: S.night, color: S.gold }}>{busy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}</span>
            <span className="uppercase">Hear it flow</span>
          </button>
          {(m.state === "ready" || m.state === "paused") && (
            <button type="button" onClick={() => (m.state === "ready" ? m.pause() : m.resume())} data-testid="sl-layers-pause" className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-[0.66rem] uppercase tracking-[0.22em]" style={{ border: `1px solid ${S.goldSoft}`, color: S.cream, fontFamily: SANS }}>
              {m.state === "ready" ? <><Pause size={14} /> Pause</> : <><Play size={14} /> Resume</>}
            </button>
          )}
          <span className="inline-flex items-center gap-1.5 text-xs" style={{ color: S.dim, fontFamily: SANS }}><Headphones size={12} /> Best with headphones</span>
        </div>
        {m.state === "error" && <p className="mt-3 text-sm" style={{ color: "#e0857a", fontFamily: SANS }} data-testid="sl-layers-error">The sound is resting for a moment. Tap again.</p>}
        <motion.p key={scene || "none"} initial={{ opacity: 0 }} animate={{ opacity: scene ? 1 : 0 }} transition={{ duration: 0.8 }}
          className="mt-8 h-10 text-2xl sm:text-3xl " style={{ fontFamily: SERIF, color: S.gold }} data-testid="sl-layers-scene">{scene}</motion.p>
        <div className="mt-4 space-y-2" data-testid="sl-layers-stack">
          {ROWS.map((r) => <Row key={r[0]} r={r} on={m.on[r[0]]} level={m.levels[r[0]]} disabled={busy} onTap={() => tap(r[0])} />)}
          <div className="grid grid-cols-2 gap-2 pt-1">
            {[["Space", "Everything grows quiet"], ["Return", "Gently, you come back"]].map(([t, d]) => (
              <div key={t} className="rounded-xl px-4 py-3" style={{ border: `1px dashed ${S.goldSoft}`, background: scene === t ? "rgba(224,169,122,0.12)" : "transparent" }} data-testid={`sl-layer-moment-${t.toLowerCase()}`}>
                <p className="text-lg" style={{ fontFamily: SERIF }}>{t}</p><p className="text-xs" style={{ fontFamily: SANS, color: S.dim }}>{d}</p>
              </div>
            ))}
          </div>
        </div>
        <Reveal className="mt-14"><p className="text-3xl sm:text-4xl font-light" style={{ fontFamily: SERIF }}>One experience, <span className="" style={{ color: S.gold }}>created around you.</span></p></Reveal>
        <div className="mt-6"><SpeakButton s={g} label="Adelaida explains" testId="sl-layers-adelaida" /></div>
      </div>
    </Section>
  );
};
